import { ContextMenuAction } from '../../explorer/models/TreeModel';
import { DatabaseTreeNode } from '../../explorer/models/DatabaseTreeModels';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { openMigrateDatabaseTab } from '../../cross-engine-migrate/openMigrateTab';
import { openDataImportTab } from '../../workspace/data-import/openDataImportTab';
import { supportsDataImportEngine } from '../../workspace/data-import/mapping/targetEligibility';
import { openQueryStoreTab, QUERY_STORE_MENU_LABEL } from '../../workspace/query-store/openQueryStoreTab';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { toast } from '../../../components/ui/toaster';
import { getScriptProvider, type DatabaseScriptType } from '../../scripting';
import { fetchPgsqlDatabaseDdl } from './objectDefinitionService';
import type { Connection } from '../../../types/electron';
import { isBetaFeatureEnabled } from '../../../core/beta';
import {
  openSchemaCompareAsSource,
  setSchemaCompareSource,
  setSchemaCompareTarget,
} from '../../workspace/schema-compare/services/schemaCompareNavigation';
import { safeConnectionAlias, safeConnectionName, safeConnectionServer } from '../../../types/safeConnectionDisplay';

/**
 * Database-specific context menu action types
 */
type DatabaseContextMenuActionType =
  | 'refresh'
  | 'new-query'
  | 'table-explorer'
  | 'schema-overview'
  | 'script-database-as'
  | 'script-database-create'
  | 'script-database-drop'
  | 'script-database-use'
  | 'script-new'
  | 'script-new-table'
  | 'script-new-view'
  | 'script-new-procedure'
  | 'script-new-function'
  | 'new-table-designer'
  | 'open-terminal'
  | 'export-bacpac'
  | 'backup-bak'
  | 'restore-bak'
  | 'pgsql-backup'
  | 'pgsql-restore'
  | 'schema-compare'
  | 'schema-compare-set-source'
  | 'schema-compare-set-target'
  | 'clone-database'
  | 'recompile-invalid'
  | 'package-explorer'
  | 'manage-loose-relationships'
  | 'migrate-database'
  | 'import-data'
  | 'database-blueprint-setup'
  | 'query-store';

const DatabaseActions = {
  REFRESH: 'refresh' as DatabaseContextMenuActionType,
  NEW_QUERY: 'new-query' as DatabaseContextMenuActionType,
  TABLE_EXPLORER: 'table-explorer' as DatabaseContextMenuActionType,
  SCHEMA_OVERVIEW: 'schema-overview' as DatabaseContextMenuActionType,
  SCRIPT_DATABASE_AS: 'script-database-as' as DatabaseContextMenuActionType,
  SCRIPT_DATABASE_CREATE: 'script-database-create' as DatabaseContextMenuActionType,
  SCRIPT_DATABASE_DROP: 'script-database-drop' as DatabaseContextMenuActionType,
  SCRIPT_DATABASE_USE: 'script-database-use' as DatabaseContextMenuActionType,
  SCRIPT_NEW: 'script-new' as DatabaseContextMenuActionType,
  SCRIPT_NEW_TABLE: 'script-new-table' as DatabaseContextMenuActionType,
  SCRIPT_NEW_VIEW: 'script-new-view' as DatabaseContextMenuActionType,
  SCRIPT_NEW_PROCEDURE: 'script-new-procedure' as DatabaseContextMenuActionType,
  SCRIPT_NEW_FUNCTION: 'script-new-function' as DatabaseContextMenuActionType,
  NEW_TABLE_DESIGNER: 'new-table-designer' as DatabaseContextMenuActionType,
  OPEN_TERMINAL: 'open-terminal' as DatabaseContextMenuActionType,
  EXPORT_BACPAC: 'export-bacpac' as DatabaseContextMenuActionType,
  BACKUP_BAK: 'backup-bak' as DatabaseContextMenuActionType,
  RESTORE_BAK: 'restore-bak' as DatabaseContextMenuActionType,
  PGSQL_BACKUP: 'pgsql-backup' as DatabaseContextMenuActionType,
  PGSQL_RESTORE: 'pgsql-restore' as DatabaseContextMenuActionType,
  SCHEMA_COMPARE: 'schema-compare' as DatabaseContextMenuActionType,
  SCHEMA_COMPARE_SET_SOURCE: 'schema-compare-set-source' as DatabaseContextMenuActionType,
  SCHEMA_COMPARE_SET_TARGET: 'schema-compare-set-target' as DatabaseContextMenuActionType,
  CLONE_DATABASE: 'clone-database' as DatabaseContextMenuActionType,
  MIGRATE_DATABASE: 'migrate-database' as DatabaseContextMenuActionType,
  IMPORT_DATA: 'import-data' as DatabaseContextMenuActionType,
  RECOMPILE_INVALID: 'recompile-invalid' as DatabaseContextMenuActionType,
  PACKAGE_EXPLORER: 'package-explorer' as DatabaseContextMenuActionType,
  MANAGE_LOOSE_RELATIONSHIPS: 'manage-loose-relationships' as DatabaseContextMenuActionType,
  SCHEMA_MIRROR_SETUP: 'database-blueprint-setup' as DatabaseContextMenuActionType,
  QUERY_STORE: 'query-store' as DatabaseContextMenuActionType,
};

/**
 * Create a separator item
 */
export function createSeparator(): ContextMenuAction {
  return {
    id: 'separator',
    label: '',
    isSeparator: true
  };
}

/**
 * Create a refresh action
 */
export function createRefreshAction(): ContextMenuAction {
  return {
    id: DatabaseActions.REFRESH,
    label: 'Refresh',
    icon: 'refresh'
  };
}

/**
 * Create new query action
 */
export function createNewQueryAction(): ContextMenuAction {
  return {
    id: DatabaseActions.NEW_QUERY,
    label: 'New Query',
    icon: 'file-plus'
  };
}

/**
 * Create table explorer action
 */
export function createTableExplorerAction(): ContextMenuAction {
  return {
    id: DatabaseActions.TABLE_EXPLORER,
    label: 'Table Explorer',
    icon: 'table'
  };
}

/**
 * Create schema overview action
 */
export function createSchemaOverviewAction(): ContextMenuAction {
  return {
    id: DatabaseActions.SCHEMA_OVERVIEW,
    label: 'Show Overview',
    icon: 'network'
  };
}

/**
 * Create script database submenu
 */
export function createScriptDatabaseSubmenu(): ContextMenuAction {
  return {
    id: DatabaseActions.SCRIPT_DATABASE_AS,
    label: 'Script Database As',
    icon: 'code',
    submenu: [
      {
        id: DatabaseActions.SCRIPT_DATABASE_CREATE,
        label: 'CREATE',
        icon: 'file-plus'
      },
      {
        id: DatabaseActions.SCRIPT_DATABASE_DROP,
        label: 'DROP',
        icon: 'trash'
      },
      {
        id: DatabaseActions.SCRIPT_DATABASE_USE,
        label: 'USE',
        icon: 'database'
      }
    ]
  };
}

/**
 * Create "Script New..." submenu for database node
 */
export function createScriptNewSubmenu(): ContextMenuAction {
  return {
    id: DatabaseActions.SCRIPT_NEW,
    label: 'Script New',
    icon: 'file-plus',
    submenu: [
      {
        id: DatabaseActions.SCRIPT_NEW_TABLE,
        label: 'Table...',
        icon: 'table'
      },
      {
        id: DatabaseActions.SCRIPT_NEW_VIEW,
        label: 'View...',
        icon: 'file-text'
      },
      {
        id: DatabaseActions.SCRIPT_NEW_PROCEDURE,
        label: 'Stored Procedure...',
        icon: 'code'
      },
      {
        id: DatabaseActions.SCRIPT_NEW_FUNCTION,
        label: 'Function...',
        icon: 'code'
      }
    ]
  };
}

/**
 * Create "New Table (Designer)..." action for database node
 */
export function createNewTableDesignerAction(): ContextMenuAction {
  return {
    id: DatabaseActions.NEW_TABLE_DESIGNER,
    label: 'New Table (Designer)...',
    icon: 'settings'
  };
}

/**
 * Create "Open Terminal" action for database node
 */
export function createOpenTerminalAction(): ContextMenuAction {
  return {
    id: DatabaseActions.OPEN_TERMINAL,
    label: 'Open Terminal',
    icon: 'terminal'
  };
}

/**
 * Create "Export Data-tier Application (.bacpac)..." action for database node
 */
export function createExportBacpacAction(): ContextMenuAction {
  return {
    id: DatabaseActions.EXPORT_BACPAC,
    label: 'Export Data-tier Application (.bacpac)...',
    icon: 'download'
  };
}

/**
 * Create "Backup Database (.bak)..." action for database node
 */
export function createBackupBakAction(): ContextMenuAction {
  return {
    id: DatabaseActions.BACKUP_BAK,
    label: 'Backup Database (.bak)...',
    icon: 'hard-drive'
  };
}

/**
 * Create "Restore Database (.bak)..." action for database node
 */
export function createRestoreBakAction(): ContextMenuAction {
  return {
    id: DatabaseActions.RESTORE_BAK,
    label: 'Restore Database (.bak)...',
    icon: 'upload'
  };
}

/**
 * Create "Backup Database (pg_dump)..." action for PostgreSQL database node
 */
export function createPgsqlBackupAction(): ContextMenuAction {
  return {
    id: DatabaseActions.PGSQL_BACKUP,
    label: 'Backup Database (pg_dump)...',
    icon: 'hard-drive'
  };
}

/**
 * Create "Restore Database..." action for PostgreSQL database node
 */
export function createPgsqlRestoreAction(): ContextMenuAction {
  return {
    id: DatabaseActions.PGSQL_RESTORE,
    label: 'Restore Database...',
    icon: 'upload'
  };
}

/**
 * Create "Schema Compare..." submenu for database node
 */
export function createSchemaCompareSubmenu(): ContextMenuAction {
  return {
    id: DatabaseActions.SCHEMA_COMPARE,
    label: 'Schema Compare...',
    icon: 'git-compare',
    submenu: [
      {
        id: DatabaseActions.SCHEMA_COMPARE,
        label: 'New Comparison...',
        icon: 'git-compare'
      },
      {
        id: DatabaseActions.SCHEMA_COMPARE_SET_SOURCE,
        label: 'Set as Source',
        icon: 'arrow-right'
      },
      {
        id: DatabaseActions.SCHEMA_COMPARE_SET_TARGET,
        label: 'Set as Target',
        icon: 'arrow-left'
      }
    ]
  };
}

/**
 * Create "Manage meta info..." action — opens the per-database MetaInfo
 * manager (loose foreign keys, JSON column declarations; future per-DB
 * metadata like descriptions, AI context, and tags will surface here too).
 */
export function createManageLooseRelationshipsAction(): ContextMenuAction {
  return {
    id: DatabaseActions.MANAGE_LOOSE_RELATIONSHIPS,
    label: 'Manage meta info...',
    icon: 'link-2',
  };
}

/**
 * Create "Migrate all tables to connection…" action — opens the cross-engine
 * migration dialog seeded with every table in this database as the source.
 */
export function createMigrateAllTablesAction(): ContextMenuAction {
  return {
    id: DatabaseActions.MIGRATE_DATABASE,
    label: 'Migrate all tables to connection...',
    icon: 'arrow-right'
  };
}

/**
 * Create "Import Data..." action — opens the Data Import wizard bound to this
 * database (no source file / target table preset). Not offered for Kusto
 * (read-only, no writable import target).
 */
export function createImportDataAction(): ContextMenuAction {
  return {
    id: DatabaseActions.IMPORT_DATA,
    label: 'Import Data...',
    icon: 'upload'
  };
}

/**
 * Create "Clone Database..." action for database node
 */
export function createCloneDatabaseAction(): ContextMenuAction {
  return {
    id: DatabaseActions.CLONE_DATABASE,
    label: 'Clone Database...',
    icon: 'copy'
  };
}

/**
 * Create "Create Database Blueprint…" action for database node — opens the Database Blueprint
 * setup wizard (folder-backed DDL export + bidirectional sync). Gated on
 * connection status by the wider menu pipeline (blueprints Schema Compare).
 */
export function createDatabaseBlueprintSetupAction(): ContextMenuAction {
  return {
    id: DatabaseActions.SCHEMA_MIRROR_SETUP,
    label: 'Create Database Blueprint...',
    icon: 'folder-git-2',
  };
}

/**
 * Generate database-specific context menu actions
 * @param engine - Optional engine type to show engine-specific actions
 */
export function generateDatabaseContextMenuActions(engine?: string): ContextMenuAction[] {
  const actions: ContextMenuAction[] = [];

  // Copy name action
  actions.push({ id: 'copy-name', label: 'Copy database name', icon: 'clipboard' });

  // Add database-specific actions
  actions.push(createSchemaOverviewAction());
  actions.push(createNewTableDesignerAction());
  actions.push(createScriptNewSubmenu());
  if (engine !== 'sqlite') {
    actions.push(createScriptDatabaseSubmenu());
  }
  actions.push(createSchemaCompareSubmenu());
  if (isBetaFeatureEnabled('cross-engine-migrate')) {
    actions.push(createMigrateAllTablesAction());
  }
  // Kusto and Azure Storage have no SQL table to import into — the Data Import
  // entry-point gate every surface shares (`supportsDataImportEngine`).
  if (supportsDataImportEngine(engine) && isBetaFeatureEnabled('data-import')) {
    actions.push(createImportDataAction());
  }
  // Database Blueprint — available on every engine. v1 (Slice 1) ships MSSQL; other
  // engines land in their own slices but the entry-point exists everywhere so
  // the wizard can show a "coming soon" hint per engine if needed.
  if (isBetaFeatureEnabled('database-blueprint')) {
    actions.push(createDatabaseBlueprintSetupAction());
  }
  // SQLite is a single-file database; cloning is best done by duplicating the file directly.
  if (engine !== 'sqlite') {
    actions.push(createCloneDatabaseAction());
  }
  actions.push(createNewQueryAction());
  actions.push(createTableExplorerAction());
  actions.push(createOpenTerminalAction());

  // Backup/restore actions - engine specific
  // Only show for engines that support backup/restore operations
  if (engine === 'mssql' || engine === 'mock') {
    // MSSQL backup options
    actions.push(createSeparator());
    actions.push(createExportBacpacAction());
    actions.push(createBackupBakAction());
    actions.push(createRestoreBakAction());
    // Query Store history/regressions for THIS database (SQL Server 2016+, Azure SQL). Read-only.
    actions.push(createSeparator());
    actions.push({ id: DatabaseActions.QUERY_STORE, label: QUERY_STORE_MENU_LABEL, icon: 'activity' });
  } else if (engine === 'pgsql') {
    // PostgreSQL backup/restore using pg_dump/pg_restore
    actions.push(createSeparator());
    actions.push(createPgsqlBackupAction());
    actions.push(createPgsqlRestoreAction());
  }
  // SQLite and other engines: no backup/restore options (SQLite files can be copied directly)

  // Oracle-specific actions
  if (engine === 'oracle') {
    actions.push(createSeparator());
    if (isBetaFeatureEnabled('package-explorer')) {
      actions.push({ id: DatabaseActions.PACKAGE_EXPLORER, label: 'Package Explorer', icon: 'package' });
    }
    actions.push({ id: DatabaseActions.RECOMPILE_INVALID, label: 'Recompile Invalid Objects', icon: 'refresh-cw' });
  }

  // MetaInfo / loose relationships — shown for every engine since
  // declarations are user-level metadata stored outside the database.
  actions.push(createSeparator());
  actions.push(createManageLooseRelationshipsAction());

  // Add separator before common actions
  actions.push(createSeparator());

  // Add common actions
  actions.push(createRefreshAction());

  return actions;
}

/**
 * Open a generated script in a new query tab
 */
function openScriptInNewTab(
  script: string,
  connectionId: string,
  database: string,
  scriptTitle: string
): void {
  const connection = getConnectionConfig(connectionId);
  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();

  addTab({
    title: `${scriptTitle}-${queryNumber}`,
    type: 'query',
    content: { query: script },
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionName(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(connection),
      database: database,
    },
  });
}

function getConnectionConfig(connectionId: string): Connection | undefined {
  const { connections, activeConnections } = useConnectionStore.getState();
  const saved = connections.find((conn) => conn.id === connectionId);
  if (saved) return saved;
  return activeConnections.find((conn) => conn.id === connectionId)?.connection;
}

/**
 * Handle script database action using Script Provider
 */
function handleScriptDatabaseAs(node: DatabaseTreeNode, scriptType: DatabaseScriptType): void {
  const connectionId = node.metadata?.connectionId;
  const databaseName = node.name;

  if (!connectionId) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const connection = getConnectionConfig(connectionId);
  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const engine = connection.engine || 'mssql';

  // PostgreSQL 19+ ships server-authored database DDL via pg_get_database_ddl.
  // Prefer it for Script-as-CREATE (exact, engine-authored) and fall back to
  // the hand-built generator when unavailable (PG < 19, connection error).
  if (scriptType === 'create' && engine === 'pgsql') {
    void handlePgsqlNativeDatabaseCreate(connectionId, databaseName);
    return;
  }

  try {
    const provider = getScriptProvider(engine);
    const script = provider.generateDatabaseScript(scriptType, {
      databaseName,
    });

    const titleMap: Record<DatabaseScriptType, string> = {
      'create': 'CREATE',
      'drop': 'DROP',
      'use': 'USE',
    };
    const scriptTitle = `${databaseName}_${titleMap[scriptType]}`;

    openScriptInNewTab(script, connectionId, databaseName, scriptTitle);
  } catch (error) {
    toast.error({
      title: 'Script Generation Error',
      description: error instanceof Error ? error.message : 'Failed to generate script',
    });
  }
}

/**
 * PostgreSQL 19+ Script Database As CREATE — try the server-native
 * pg_get_database_ddl() first, fall back to the hand-built generator when the
 * function is unavailable. A header comment marks the server-authored path.
 */
async function handlePgsqlNativeDatabaseCreate(connectionId: string, databaseName: string): Promise<void> {
  let native: string | null = null;
  try {
    native = await fetchPgsqlDatabaseDdl({ connectionId, database: databaseName });
  } catch {
    native = null;
  }

  // Same toast contract as the sync path above — without this, a generator
  // throw in this async handler would be a silent unhandled rejection.
  try {
    let script: string;
    if (native) {
      script = `-- Generated by PostgreSQL (pg_get_database_ddl)\n${native}`;
    } else {
      const provider = getScriptProvider('pgsql');
      script = provider.generateDatabaseScript('create', { databaseName });
    }

    openScriptInNewTab(script, connectionId, databaseName, `${databaseName}_CREATE`);
  } catch (error) {
    toast.error({
      title: 'Script Generation Error',
      description: error instanceof Error ? error.message : 'Failed to generate script',
    });
    return;
  }

  window.electronAPI?.analytics
    ?.track('feature:script_native_ddl', {
      engine: 'pgsql',
      target: 'database',
      native: native !== null,
    })
    .catch(() => {
      // analytics is fire-and-forget
    });
}

/**
 * Handle new query action
 */
function handleNewQuery(node: DatabaseTreeNode): void {
  const connectionId = node.metadata?.connectionId;
  const databaseName = node.name;

  if (!connectionId) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const connection = getConnectionConfig(connectionId);
  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();

  addTab({
    title: `Query-${queryNumber}`,
    type: 'query',
    content: { query: '' },
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionName(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(connection),
      database: databaseName,
    },
  });
}

/**
 * Handle table explorer action - opens the Table Explorer dialog
 */
function handleTableExplorer(node: DatabaseTreeNode): void {
  const connectionId = node.metadata?.connectionId;
  const databaseName = node.name;

  if (!connectionId) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const connection = getConnectionConfig(connectionId);
  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const engine = connection.engine || 'mssql';

  // Import and use the dialog store to open the Table Explorer dialog
  // This ensures consistent UX - user always selects a table first via dialog
  import('../../../features/workspace/table-explorer/store/tableExplorerDialogStore').then(
    ({ useTableExplorerDialogStore }) => {
      useTableExplorerDialogStore.getState().openDialog({
        connectionId,
        connectionName: safeConnectionName(connection, 'Unnamed Connection'),
        serverHost: safeConnectionServer(connection),
        database: databaseName,
        engineId: engine as 'mssql' | 'pgsql' | 'sqlite',
      });
    }
  );
}

/**
 * Handle Schema Overview action - opens Schema Overview tab
 */
function handleSchemaOverview(node: DatabaseTreeNode): void {
  const connectionId = node.metadata?.connectionId;
  const databaseName = node.name;

  if (!connectionId) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const connection = getConnectionConfig(connectionId);
  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const { addTab } = useWorkspaceStore.getState();

  addTab({
    title: `Overview: ${databaseName}`,
    type: 'schema-overview',
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionName(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(connection),
      database: databaseName,
    },
    content: {
      connectionId,
      databaseName,
      filterMode: 'all',
      isLoading: true,
    },
  });
}

/**
 * Handle New Table (Designer) action - opens Table Designer in create mode
 */
function handleNewTableDesigner(node: DatabaseTreeNode): void {
  const connectionId = node.metadata?.connectionId;
  const databaseName = node.name;

  if (!connectionId) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const connection = getConnectionConfig(connectionId);
  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const engine = connection.engine || 'mssql';
  const defaultSchema = engine === 'pgsql' ? 'public' : 'dbo';

  const { addTab } = useWorkspaceStore.getState();

  addTab({
    title: 'New Table',
    type: 'table-designer',
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionName(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(connection),
      database: databaseName,
    },
    content: {
      mode: 'create',
      tableName: '',
      schema: defaultSchema,
      database: databaseName,
      columns: [],
      isDirty: false,
      isLoading: false,
    },
  });
}

/**
 * Execute a database context menu action
 */
/**
 * Handle "Migrate all tables to connection…" — opens the cross-engine migration
 * as a dedicated workspace tab, seeded with this database (every table) as the
 * source. A whole-database migration can be long-running, so it gets its own
 * surface with live per-table progress + cancel.
 */
function handleMigrateAllTables(node: DatabaseTreeNode): void {
  const connectionId = node.metadata?.connectionId;
  const databaseName = node.name;
  if (!connectionId) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }
  const connection = getConnectionConfig(connectionId);
  const engine = connection?.engine || 'mssql';
  openMigrateDatabaseTab({
    connectionId,
    connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
    serverHost: safeConnectionServer(connection),
    database: databaseName,
    engine,
  });
}

/** Handle "Query Store..." — opens (or focuses) the Query Store tab for this database. */
function handleOpenQueryStore(node: DatabaseTreeNode): void {
  const connectionId = node.metadata?.connectionId;
  if (!connectionId) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }
  const connection = getConnectionConfig(connectionId);
  openQueryStoreTab({
    entry: 'context-menu',
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(connection),
      database: node.name,
    },
  });
}

/**
 * Handle "Import Data..." — opens the Data Import wizard bound to this
 * database (no source file or target table preset; the wizard's own Source
 * and Target steps collect those).
 */
function handleImportData(node: DatabaseTreeNode): void {
  const connectionId = node.metadata?.connectionId;
  if (!connectionId) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }
  const connection = getConnectionConfig(connectionId);
  openDataImportTab({
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(connection),
      database: node.name,
    },
  });
  window.electronAPI?.analytics?.track('feature:data_import_open', { source: 'db_menu' }).catch(() => {
    // analytics is fire-and-forget
  });
}

export function executeDatabaseContextMenuAction(
  actionId: string,
  nodeId: string,
  handlers: {
    onRefresh?: (nodeId: string) => void;
    onOpenTerminal?: (connectionId: string, database: string) => void;
    onExportBacpac?: (connectionId: string, database: string) => void;
    onBackupBak?: (connectionId: string, database: string) => void;
    onRestoreBak?: (connectionId: string, database: string) => void;
    onPgsqlBackup?: (connectionId: string, database: string) => void;
    onPgsqlRestore?: (connectionId: string, database: string) => void;
    onCloneDatabase?: (connectionId: string, database: string) => void;
  } = {},
  node?: DatabaseTreeNode
): void {
  // Handle refresh separately
  if (actionId === DatabaseActions.REFRESH) {
    if (handlers.onRefresh) {
      handlers.onRefresh(nodeId);
    }
    return;
  }

  switch (actionId) {
    case DatabaseActions.SCRIPT_DATABASE_CREATE:
      if (node) {
        handleScriptDatabaseAs(node, 'create');
      } else {
        toast.error({ title: 'Error', description: 'Database node not found' });
      }
      break;

    case DatabaseActions.SCRIPT_DATABASE_DROP:
      if (node) {
        handleScriptDatabaseAs(node, 'drop');
      } else {
        toast.error({ title: 'Error', description: 'Database node not found' });
      }
      break;

    case DatabaseActions.SCRIPT_DATABASE_USE:
      if (node) {
        handleScriptDatabaseAs(node, 'use');
      } else {
        toast.error({ title: 'Error', description: 'Database node not found' });
      }
      break;

    case DatabaseActions.NEW_QUERY:
      if (node) {
        handleNewQuery(node);
      } else {
        toast.error({ title: 'Error', description: 'Database node not found' });
      }
      break;

    case DatabaseActions.TABLE_EXPLORER:
      if (node) {
        handleTableExplorer(node);
      } else {
        toast.error({ title: 'Error', description: 'Database node not found' });
      }
      break;

    case DatabaseActions.SCHEMA_OVERVIEW:
      if (node) {
        handleSchemaOverview(node);
      } else {
        toast.error({ title: 'Error', description: 'Database node not found' });
      }
      break;

    case DatabaseActions.NEW_TABLE_DESIGNER:
      if (node) {
        handleNewTableDesigner(node);
      } else {
        toast.error({ title: 'Error', description: 'Database node not found' });
      }
      break;

    case DatabaseActions.MIGRATE_DATABASE:
      if (node) {
        handleMigrateAllTables(node);
      } else {
        toast.error({ title: 'Error', description: 'Database node not found' });
      }
      break;

    case DatabaseActions.IMPORT_DATA:
      if (node) {
        handleImportData(node);
      } else {
        toast.error({ title: 'Error', description: 'Database node not found' });
      }
      break;

    case DatabaseActions.QUERY_STORE:
      if (node) {
        handleOpenQueryStore(node);
      } else {
        toast.error({ title: 'Error', description: 'Database node not found' });
      }
      break;

    case DatabaseActions.OPEN_TERMINAL:
      if (node && handlers.onOpenTerminal) {
        const connectionId = node.metadata?.connectionId;
        const databaseName = node.name;
        if (connectionId) {
          handlers.onOpenTerminal(connectionId, databaseName);
        } else {
          toast.error({ title: 'Error', description: 'Connection not found' });
        }
      } else if (!handlers.onOpenTerminal) {
        toast.error({ title: 'Error', description: 'Terminal handler not available' });
      }
      break;

    case DatabaseActions.EXPORT_BACPAC:
      if (node && handlers.onExportBacpac) {
        const connectionId = node.metadata?.connectionId;
        const databaseName = node.name;
        if (connectionId) {
          handlers.onExportBacpac(connectionId, databaseName);
        } else {
          toast.error({ title: 'Error', description: 'Connection not found' });
        }
      } else if (!handlers.onExportBacpac) {
        toast.error({ title: 'Error', description: 'Export handler not available' });
      }
      break;

    case DatabaseActions.BACKUP_BAK:
      if (node && handlers.onBackupBak) {
        const connectionId = node.metadata?.connectionId;
        const databaseName = node.name;
        if (connectionId) {
          handlers.onBackupBak(connectionId, databaseName);
        } else {
          toast.error({ title: 'Error', description: 'Connection not found' });
        }
      } else if (!handlers.onBackupBak) {
        toast.error({ title: 'Error', description: 'Backup handler not available' });
      }
      break;

    case DatabaseActions.RESTORE_BAK:
      if (node && handlers.onRestoreBak) {
        const connectionId = node.metadata?.connectionId;
        const databaseName = node.name;
        if (connectionId) {
          handlers.onRestoreBak(connectionId, databaseName);
        } else {
          toast.error({ title: 'Error', description: 'Connection not found' });
        }
      } else if (!handlers.onRestoreBak) {
        toast.error({ title: 'Error', description: 'Restore handler not available' });
      }
      break;

    case DatabaseActions.PGSQL_BACKUP:
      if (node && handlers.onPgsqlBackup) {
        const connectionId = node.metadata?.connectionId;
        const databaseName = node.name;
        if (connectionId) {
          handlers.onPgsqlBackup(connectionId, databaseName);
        } else {
          toast.error({ title: 'Error', description: 'Connection not found' });
        }
      } else if (!handlers.onPgsqlBackup) {
        toast.error({ title: 'Error', description: 'PostgreSQL backup handler not available' });
      }
      break;

    case DatabaseActions.PGSQL_RESTORE:
      if (node && handlers.onPgsqlRestore) {
        const connectionId = node.metadata?.connectionId;
        const databaseName = node.name;
        if (connectionId) {
          handlers.onPgsqlRestore(connectionId, databaseName);
        } else {
          toast.error({ title: 'Error', description: 'Connection not found' });
        }
      } else if (!handlers.onPgsqlRestore) {
        toast.error({ title: 'Error', description: 'PostgreSQL restore handler not available' });
      }
      break;

    case DatabaseActions.SCHEMA_COMPARE:
      if (node) {
        const connectionId = node.metadata?.connectionId;
        const databaseName = node.name;
        if (connectionId) {
          openSchemaCompareAsSource(connectionId, databaseName);
        } else {
          toast.error({ title: 'Error', description: 'Connection not found' });
        }
      } else {
        toast.error({ title: 'Error', description: 'Database node not found' });
      }
      break;

    case DatabaseActions.SCHEMA_COMPARE_SET_SOURCE:
      if (node) {
        const connectionId = node.metadata?.connectionId;
        const databaseName = node.name;
        if (connectionId) {
          setSchemaCompareSource(connectionId, databaseName);
        } else {
          toast.error({ title: 'Error', description: 'Connection not found' });
        }
      } else {
        toast.error({ title: 'Error', description: 'Database node not found' });
      }
      break;

    case DatabaseActions.SCHEMA_COMPARE_SET_TARGET:
      if (node) {
        const connectionId = node.metadata?.connectionId;
        const databaseName = node.name;
        if (connectionId) {
          setSchemaCompareTarget(connectionId, databaseName);
        } else {
          toast.error({ title: 'Error', description: 'Connection not found' });
        }
      } else {
        toast.error({ title: 'Error', description: 'Database node not found' });
      }
      break;

    case DatabaseActions.CLONE_DATABASE:
      if (node && handlers.onCloneDatabase) {
        const connectionId = node.metadata?.connectionId;
        const databaseName = node.name;
        if (connectionId) {
          handlers.onCloneDatabase(connectionId, databaseName);
        } else {
          toast.error({ title: 'Error', description: 'Connection not found' });
        }
      } else if (!handlers.onCloneDatabase) {
        toast.error({ title: 'Error', description: 'Clone database handler not available' });
      }
      break;

    case DatabaseActions.PACKAGE_EXPLORER:
      if (node) {
        const connectionId = node.metadata?.connectionId;
        const databaseName = node.name;
        if (connectionId) {
          handlePackageExplorer(connectionId, databaseName);
        } else {
          toast.error({ title: 'Error', description: 'Connection not found' });
        }
      } else {
        toast.error({ title: 'Error', description: 'Database node not found' });
      }
      break;

    case DatabaseActions.MANAGE_LOOSE_RELATIONSHIPS:
      if (node) {
        const connectionId = node.metadata?.connectionId;
        const databaseName = node.name;
        if (connectionId) {
          handleManageLooseRelationships(connectionId, databaseName);
        } else {
          toast.error({ title: 'Error', description: 'Connection not found' });
        }
      } else {
        toast.error({ title: 'Error', description: 'Database node not found' });
      }
      break;

    case DatabaseActions.SCHEMA_MIRROR_SETUP:
      if (node) {
        const connectionId = node.metadata?.connectionId;
        const databaseName = node.name;
        if (connectionId) {
          handleDatabaseBlueprintSetup(connectionId, databaseName);
        } else {
          toast.error({ title: 'Error', description: 'Connection not found' });
        }
      } else {
        toast.error({ title: 'Error', description: 'Database node not found' });
      }
      break;

    case DatabaseActions.RECOMPILE_INVALID:
      if (node) {
        const connectionId = node.metadata?.connectionId;
        const schema = node.name;
        if (connectionId) {
          handleRecompileInvalid(connectionId, schema);
        } else {
          toast.error({ title: 'Error', description: 'Connection not found' });
        }
      } else {
        toast.error({ title: 'Error', description: 'Database node not found' });
      }
      break;

    default:
      // Ignore unknown actions (submenu headers, etc.)
      break;
  }
}

function handleDatabaseBlueprintSetup(connectionId: string, database: string): void {
  void (async () => {
    const { useDatabaseBlueprintSetupStore } = await import('../../database-blueprint');
    const connection = getConnectionConfig(connectionId);
    if (!connection) {
      toast.error({ title: 'Error', description: 'Connection not found' });
      return;
    }
    // Mock connections present MSSQL-shaped fixtures, so treat 'mock' as
    // 'mssql' for emitter dispatch + dialog labelling.
    const rawEngine = connection.engine ?? 'mssql';
    const engine = (rawEngine === 'mock' ? 'mssql' : rawEngine) as
      | 'mssql'
      | 'pgsql'
      | 'mysql'
      | 'oracle'
      | 'sqlite';
    useDatabaseBlueprintSetupStore.getState().openSetupDialog({
      connectionId,
      connectionName: safeConnectionName(connection, 'Unnamed Connection'),
      database,
      engine,
    });
  })();
}

function handleManageLooseRelationships(connectionId: string, database: string): void {
  void (async () => {
    const { useMetaInfoManagerDialogStore } = await import(
      '../../workspace/common/components/meta-info/metaInfoManagerDialogStore'
    );
    useMetaInfoManagerDialogStore.getState().openDialog({ connectionId, database });
    window.electronAPI?.analytics
      ?.track('feature:metainfo_manager_open', { source: 'database_context_menu' })
      .catch(() => {
        // analytics is fire-and-forget
      });
  })();
}

function handlePackageExplorer(connectionId: string, database: string): void {
  void (async () => {
    const { findExistingPackageExplorerTab, createDefaultPackageExplorerContent } =
      await import('../../workspace/package-explorer/types');
    const { tabs, addTab, setActiveTab } = useWorkspaceStore.getState();
    const existing = findExistingPackageExplorerTab(tabs, connectionId, database);
    if (existing) {
      setActiveTab(existing.id);
    } else {
      const connection = getConnectionConfig(connectionId);
      addTab({
        title: `Package Explorer — ${database}`,
        type: 'package-explorer',
        connectionInfo: {
          connectionId,
          connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
          serverHost: safeConnectionServer(connection),
          database,
        },
        content: createDefaultPackageExplorerContent({ connectionId, database }),
      });
    }
  })();
}

function handleRecompileInvalid(connectionId: string, schema: string): void {
  toast.default({ title: 'Recompiling...', description: `Recompiling invalid objects in ${schema}...` });

  void (async () => {
    try {
      const result = await window.electronAPI!.oraclePackage.recompileInvalid({ connectionId, schema });
      if (result.success) {
        if (result.total === 0) {
          toast.success({ title: 'No Invalid Objects', description: `No invalid objects found in ${schema}` });
        } else {
          const msg = `${result.succeeded} succeeded, ${result.failed} still invalid`;
          if (result.failed > 0) {
            toast.warning({ title: `Recompiled ${result.total} objects`, description: msg });
          } else {
            toast.success({ title: `Recompiled ${result.total} objects`, description: msg });
          }
        }
      } else {
        toast.error({ title: 'Recompile Failed', description: result.error ?? 'Unknown error' });
      }
    } catch (error) {
      toast.error({ title: 'Recompile Failed', description: error instanceof Error ? error.message : 'Unknown error' });
    }
  })();
}
